import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react';
import { message } from 'antd';
import type { SessionHistoryItem } from '@/pages/SessionHistory/types';
import { AGENT_CONVERSATION_PAGE_SIZE, LOAD_MORE_THRESHOLD_PX, compactParams, mergeConversationListItems, normalizeAgentConversationListData, resolveConversationCursorState } from '@/components/HistoryMenu/util';

type AgentConversationListResponse = {
  code?: number;
  msg?: string;
  data?: API.AgentConversationListData;
};

interface UseAgentConversationCursorListOptions {
  fetcher: (params: API.AgentConversationListParams) => Promise<AgentConversationListResponse>;
  params?: Partial<API.AgentConversationListParams>;
  /** container 模式：滚动容器 onScroll/wheel + IntersectionObserver；viewport 模式：页面滚动 + sentinel */
  observerRoot?: 'container' | 'viewport';
  loadMoreThresholdPx?: number;
  autoLoad?: boolean;
  debounceMs?: number;
  errorMessage?: string;
  loadMoreErrorMessage?: string;
  onListLoaded?: (items: SessionHistoryItem[], meta: { isReset: boolean }) => void;
  onResetError?: () => void;
}

const EMPTY_LIST_PARAMS: Partial<API.AgentConversationListParams> = {};
const SCROLL_BOTTOM_EPSILON_PX = 2;
const CONTAINER_LOAD_MORE_THRESHOLD_PX = 20;
const LOAD_MORE_ROOT_MARGIN_BOTTOM = (threshold: number) => `0px 0px ${threshold}px 0px`;

const useAgentConversationCursorList = ({ fetcher, params = EMPTY_LIST_PARAMS, observerRoot = 'viewport', loadMoreThresholdPx, autoLoad = true, debounceMs = 0, errorMessage = '列表加载失败', loadMoreErrorMessage = '加载更多失败', onListLoaded, onResetError }: UseAgentConversationCursorListOptions) => {
  const loadMoreThreshold = loadMoreThresholdPx ?? (observerRoot === 'container' ? CONTAINER_LOAD_MORE_THRESHOLD_PX : LOAD_MORE_THRESHOLD_PX);
  const loadMoreLockRef = useRef(false);
  const nextCursorRef = useRef<string | undefined>();
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const hasUserScrolledRef = useRef(false);
  const onListLoadedRef = useRef(onListLoaded);
  const onResetErrorRef = useRef(onResetError);
  const [items, setItems] = useState<SessionHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [scrollContainerReady, setScrollContainerReady] = useState(false);

  onListLoadedRef.current = onListLoaded;
  onResetErrorRef.current = onResetError;
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;

  const bindScrollContainer = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
    setScrollContainerReady(!!node);
  }, []);

  const applyCursorState = useCallback((nextCursor?: string, nextHasMore = false) => {
    nextCursorRef.current = nextCursor;
    hasMoreRef.current = nextHasMore;
    setHasMore(nextHasMore);
  }, []);

  const buildRequestParams = useCallback(
    (cursor?: string) =>
      compactParams({
        page_size: AGENT_CONVERSATION_PAGE_SIZE,
        ...params,
        ...(cursor ? { cursor } : {}),
      }),
    [params],
  );

  const reload = useCallback(async () => {
    hasUserScrolledRef.current = false;
    loadMoreLockRef.current = true;
    setLoading(true);
    applyCursorState(undefined, false);
    try {
      const res = await fetcher(buildRequestParams());
      const data = normalizeAgentConversationListData(res?.data);
      const cursorState = resolveConversationCursorState(data);
      setItems(data.items);
      applyCursorState(cursorState.nextCursor, cursorState.hasMore);
      onListLoadedRef.current?.(data.items, { isReset: true });
    } catch (error) {
      setItems([]);
      applyCursorState(undefined, false);
      onResetErrorRef.current?.();
      message.error(errorMessage);
    } finally {
      loadMoreLockRef.current = false;
      setLoading(false);
    }
  }, [applyCursorState, buildRequestParams, errorMessage, fetcher]);

  const loadMore = useCallback(async () => {
    if (loadMoreLockRef.current || loadingRef.current || loadingMoreRef.current || !hasMoreRef.current || !nextCursorRef.current) {
      return;
    }

    loadMoreLockRef.current = true;
    setLoadingMore(true);
    const cursor = nextCursorRef.current;
    try {
      const res = await fetcher(buildRequestParams(cursor));
      const data = normalizeAgentConversationListData(res?.data);
      const cursorState = resolveConversationCursorState(data);
      let mergedItems: SessionHistoryItem[] = [];
      setItems((previous) => {
        mergedItems = mergeConversationListItems(previous, data.items);
        return mergedItems;
      });
      applyCursorState(cursorState.nextCursor, cursorState.hasMore);
      onListLoadedRef.current?.(mergedItems, { isReset: false });
    } catch (error) {
      message.error(loadMoreErrorMessage);
    } finally {
      loadMoreLockRef.current = false;
      setLoadingMore(false);
    }
  }, [applyCursorState, buildRequestParams, fetcher, loadMoreErrorMessage]);

  const isNearBottom = useCallback(
    (el: HTMLElement) => {
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      return distanceToBottom <= loadMoreThreshold + SCROLL_BOTTOM_EPSILON_PX;
    },
    [loadMoreThreshold],
  );

  const checkNearBottomAndLoad = useCallback(
    (el: HTMLElement) => {
      hasUserScrolledRef.current = true;
      if (isNearBottom(el)) {
        void loadMore();
      }
    },
    [isNearBottom, loadMore],
  );

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      scrollContainerRef.current = el;
      checkNearBottomAndLoad(el);
    },
    [checkNearBottomAndLoad],
  );

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    if (debounceMs <= 0) {
      void reload();
      return;
    }

    const timer = window.setTimeout(() => {
      void reload();
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [autoLoad, debounceMs, reload]);

  // container：列表已到底时继续滚不会触发 scroll，用 wheel/touchmove 兜底
  useEffect(() => {
    if (observerRoot !== 'container') {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      hasUserScrolledRef.current = true;
      if (event.deltaY <= 0) {
        return;
      }
      if (isNearBottom(container)) {
        void loadMore();
      }
    };

    const onTouchMove = () => {
      hasUserScrolledRef.current = true;
      if (isNearBottom(container)) {
        void loadMore();
      }
    };

    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchmove', onTouchMove);
    };
  }, [isNearBottom, loadMore, observerRoot, scrollContainerReady]);

  useEffect(() => {
    if (observerRoot !== 'viewport') {
      return;
    }

    const onPageScroll = () => {
      hasUserScrolledRef.current = true;
    };

    document.addEventListener('scroll', onPageScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('scroll', onPageScroll, { capture: true });
    };
  }, [observerRoot]);

  // viewport / container 共用 sentinel observer；container 以滚动容器为 root
  useEffect(() => {
    if (!hasMore || loading) {
      return;
    }

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) {
      return;
    }

    const root = observerRoot === 'container' ? scrollContainerRef.current : null;
    if (observerRoot === 'container' && !root) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        if (!hasUserScrolledRef.current) {
          return;
        }
        void loadMore();
      },
      {
        root,
        rootMargin: LOAD_MORE_ROOT_MARGIN_BOTTOM(loadMoreThreshold),
        threshold: 0,
      },
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, items.length, loadMore, loadMoreThreshold, loading, loadingMore, observerRoot, scrollContainerReady]);

  return {
    items,
    setItems,
    loading,
    loadingMore,
    hasMore,
    reload,
    loadMore,
    handleScroll,
    bindScrollContainer,
    loadMoreSentinelRef,
    loadMoreThreshold,
  };
};

export default useAgentConversationCursorList;
